import { Actions, Button, CheckField, Notice, TextField } from '@trellistry/ui';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { encode } from 'uqr';
import { confirmAuthenticator, enrolAuthenticator, setPassword, type PasswordProblem } from '../api.js';
import { signInFailure } from './sign-in-messages.js';

/**
 * The steps first-time setup and recovery share: choosing a password, setting up the authenticator app, and keeping
 * the backup codes. Each works from a grant (design §5); the authenticator's secret and the codes are shown once.
 */
const problems: Record<PasswordProblem, string> = {
  too_short: 'Please use at least 12 characters. A short sentence works well.',
  too_long: 'That’s longer than we can accept. Please use something shorter.',
  contains_email: 'Please choose a password that doesn’t include your email address.',
  breached: 'That password has appeared in a data breach elsewhere. Please choose another.',
};

export function PasswordStep({ grantId, onDone, onExpired }: { grantId: string; onDone: () => void; onExpired: () => void }) {
  const [password, setValue] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== again) { setError('Those two passwords don’t match.'); return; }
    setBusy(true);
    try {
      const r = await setPassword(grantId, password);
      if (r.status === 'ok') onDone();
      else if (r.status === 'rejected') setError(problems[r.problem] ?? 'Please choose another password.');
      else onExpired();
    } catch (e) {
      setError(signInFailure(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1>Choose your password</h1>
      <p className="muted">Only you will know it. A short sentence you’ll remember works well.</p>
      {error && <Notice tone="error">{error}</Notice>}
      <TextField label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setValue(e.target.value)} required autoFocus />
      <TextField label="The same password again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
      <Actions><Button type="submit" busy={busy} busyLabel="Saving…">Continue</Button></Actions>
    </form>
  );
}

/** The otpauth address as a QR code, drawn as one SVG path so nothing is injected as HTML. */
function QrCode({ text }: { text: string }) {
  const path = useMemo(() => {
    const { data, size } = encode(text, { border: 2 });
    const parts: string[] = [];
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (data[y]?.[x]) parts.push(`M${x} ${y}h1v1h-1z`);
    return { d: parts.join(''), size };
  }, [text]);
  return (
    <svg className="qr" viewBox={`0 0 ${path.size} ${path.size}`} role="img" aria-label="A code to scan with your authenticator app">
      <rect width={path.size} height={path.size} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
}

export function AuthenticatorStep({ grantId, onConfirmed, onExpired }: { grantId: string; onConfirmed: (backupCodes: string[]) => void; onExpired: () => void }) {
  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const asked = useRef(false);   // each request makes a new secret, and React may run an effect twice in development

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    enrolAuthenticator(grantId)
      .then((r) => { if (r.status === 'ok') setEnrolment({ secret: r.secret, uri: r.uri }); else onExpired(); })
      .catch((e: unknown) => setError(signInFailure(e)));
  }, [grantId, onExpired]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await confirmAuthenticator(grantId, code.replace(/\s/g, ''));
      if (r.status === 'ok') onConfirmed(r.backupCodes);
      else if (r.status === 'failed') setError('That code didn’t match. Check the time on your phone and try the next code.');
      else onExpired();
    } catch (e) {
      setError(signInFailure(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1>Set up your authenticator app</h1>
      <p className="muted">Open an authenticator app on your phone, such as Google Authenticator or Microsoft Authenticator, and scan this code.</p>
      {error && <Notice tone="error">{error}</Notice>}
      {enrolment ? (
        <>
          <QrCode text={enrolment.uri} />
          <details>
            <summary>Can’t scan it?</summary>
            <p className="muted">Choose to enter a key in your app, and type this:</p>
            <p className="secret">{enrolment.secret}</p>
          </details>
          <TextField label="The six-digit code your app shows" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
          <Actions><Button type="submit" busy={busy} busyLabel="Checking…">Continue</Button></Actions>
        </>
      ) : !error && <p className="muted">One moment…</p>}
    </form>
  );
}

/** Backup codes, shown once: here during setup, and under My sign-in when replaced. */
export function CodeList({ codes }: { codes: string[] }) {
  return <ul className="codes">{codes.map((c) => <li key={c}>{c}</li>)}</ul>;
}

export function BackupCodesStep({ codes, onDone, doneLabel = 'Continue' }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const [kept, setKept] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <h1>Keep your backup codes</h1>
      <p className="muted">If you lose your phone, each of these codes lets you in once. We’ll show them only now, so keep them somewhere safe, away from your phone.</p>
      <CodeList codes={codes} />
      <Button variant="quiet" onClick={async () => { await navigator.clipboard.writeText(codes.join('\n')); setCopied(true); }}>{copied ? 'Copied' : 'Copy the codes'}</Button>
      <CheckField label="I’ve kept these somewhere safe" checked={kept} onChange={(e) => setKept(e.target.checked)} />
      <Button disabled={!kept} onClick={onDone}>{doneLabel}</Button>
    </div>
  );
}
